<template>
    <div class="page integrations">
        <h1>Integrations</h1>
        <p>
            The best part about Open Data is all the awesome uses it can support downstream.
            And people have found <em>lots</em> of uses, making Unpaywall a trusted, world-leading authority on Open Access.
            Here’s a list of organizations who are currently using Unpaywall data:
        </p>

        <h2 id="search">Citation indices</h2>
        <ul>
            <li><a href="https://app.dimensions.ai/discover/publication?and_facet_open_access=True">Dimensions</a></li>
            <li><a href="https://www.litmaps.co/">Litmaps</a></li>
            <li><a href="https://www.lens.org/lens/search/scholar/list">Lens</a></li>
            <li><a href="https://www.qeios.com/discover">Qeios</a></li>
            <li><a href="https://scite.ai/">scite</a></li>
            <li><a href="https://blog.impactstory.org/elsevier-data-feed/">Scopus</a></li>
            <li><a href="https://images.webofknowledge.com/images/help/WOS/hp_whatsnew_wos.html#ts_whatsnew_overview">Web of Science</a></li>
        </ul>


        <h2 id="discover">Discovery tools</h2>
        <ul>
            <li><a href="https://www.cloudsourceoa.com/">CloudSource OA</a></li>
            <li><a href="https://cloud.ebsco.com/apps/unpaywall">EBSCO Discovery System (EDS)</a></li>
            <li><a href="http://blog.europepmc.org/2018/04/unlocking-open-europe-pmc-integrates.html">Europe PubMed Central</a></li>
            <li><a href="https://gettheresearch.org">Get The Research</a></li>
            <li>
                ProQuest <a href="https://knowledge.exlibrisgroup.com/Alma/Knowledge_Articles/How_to_Enable_the_Unpaywall_API_Integration">Alma, </a>
                <a href="https://developers.exlibrisgroup.com/appcenter/unpaywall/">Primo, </a> and
                <a href="https://ideas.exlibrisgroup.com/forums/564566-summon/suggestions/36261826-add-unpaywall-database-to-the-summon-index">Summon</a>
            </li>
            <li><a href="https://help.oclc.org/Discovery_and_Reference/WorldCat_Discovery/Search_results/Open_content_in_WorldCat_Discovery_search_results#Open_Access_DOI_links_enhanced_by_Unpaywall">OCLC WorldCat Discovery</a></li>
            <li><a href="https://pubhive.com/home">PubHive Navigator</a></li>
            <li><a href="https://blog.scienceopen.com/category/aggregation/">ScienceOpen</a></li>


            <li><a href="https://tdnet.io/services/discovery">TDNet</a></li>
            <li><a href="https://www.tripdatabase.com/">Trip Database</a></li>
        </ul>


        <h2 id="link-resolvers">Library link resolvers</h2>
        <ul>
            <li>
                <a href="https://knowledge.exlibrisgroup.com/360_Services/360_Link/0Product_Documentation/Overview/360_Link_with_IEDL%3A_Open_Access_Lookup_Service_Integration">360
                    Link</a>
            </li>
            <li><a href="https://knowledge.exlibrisgroup.com/SFX/Knowledge_Articles/How_to_enable_oaDOI_service_on_SFX">SFX</a>
            </li>
            <li>
                <a href="https://cloud.ebsco.com/apps/unpaywall">EBSCO FTF</a>
            </li>
            <li>Roll your own: the shortcut link <code>https://unpaywall.org/YOUR_DOI</code> automatically redirects to
                an open version of <code>YOUR_DOI</code> if we have one; many libraries have added this as a manual
                target in their own link resolvers.
            </li>

        </ul>

        <h2>Usage in Funding Analysis</h2>
        <p>These funders have are all using Unpaywall to help track the openness of the research they fund, and improve their efforts to promote Open Access:</p>
        <ul>
            <li>US National Institutes of Health</li>
            <li>UK REF 2021 (Research Excellence Framework)</li>
            <li>French Ministry of Higher Education & Research</li>
            <li>Swiss National Science Foundation</li>
            <li>German Federal Ministry of Education and Research</li>
            <li>Science Foundation Ireland</li> <!-- https://twitter.com/scienceirel/status/1154055965580259328?s=20 -->
            <li>The Wellcome Trust</li>
        </ul>

        <h2>Online OA Dashboards</h2>
        <ul>
            <li><a href="https://najkoja.shinyapps.io/hybridoa/">Hybrid OA Monitor</a></li>
            <li>
                <a href="https://ec.europa.eu/info/research-and-innovation/strategy/goals-research-and-innovation-policy/open-science/open-science-monitor/trends-open-access-publications_en">European Open Science Monitor</a></li>
            <li><a href="https://open-access-monitor.de/#/publications">German Open Access Monitor</a></li>
        </ul>

        <h2>Browser extensions</h2>
        <ul>
            <li><a href="https://click.endnote.com/">EndNote Click</a></li>
          <li><a href="https://www.leanlibrary.com/">Lean Library</a> (<a href="/case-studies/lean-library.pdf"><strong>read the case study</strong></a>)</li>
            <li><a href="https://thirdiron.com/products/libkey-nomad/">LibKey Nomad</a></li>
            <li><a href="https://openaccessbutton.org/">Open Access Button</a></li>
            <li><a href="https://pubhive.com/browser-plug-ins">PubHive Navigator</a></li>
            <li>
                <a href="https://chrome.google.com/webstore/detail/unpaywall/iplffkdpngmdjhlpjmppncnlhomiipha?hl=en">Unpaywall browser extension</a></li>
            <li>
                <a href="https://www.oahelper.org/">Open Access Helper</a>
            </li>
        </ul>

        <h2>Machine learning and AI</h2>
        <ul>
            <li><a href="https://www.facten.com/">Facten</a></li>
            <li><a href="https://www.nature.com/articles/d41586-019-02142-1">Jawaharlal Nehru University Data Depot</a></li>
            <li><a href="http://machineintelligence.se/">Machine Intelligence Sweden</a></li>
            <li><a href="https://mitpress.mit.edu/kfg">MIT Knowledge Futures Group</a></li>
            <li><a href="https://pubhive.com/home">PubHive Navigator</a></li>
            <li><a href="https://researchably.com/">Researchably</a></li>
            <li><a href="https://www.scholarcy.com/">Scholarcy</a></li>
            <li><a href="https://www.semanticscholar.org/">Semantic Scholar</a></li>
            <li><a href="https://sorcero.com">Sorcero</a></li>
            <li><a href="https://www.wizdom.ai/">Wizdom.ai</a></li>
        </ul>

        <h2 id="other">Other integrations</h2>
        <ul>
            <li>
                <a href="https://thirdiron.com/products/browzine/">Browzine</a>
            </li>
            <li>
                <a href="https://blog.openaccessbutton.org/new-powerfully-simple-library-tool-to-deliver-articles-no-subscription-needed-1c8b7def604f">InstantILL</a> (Interlibrary loan software from Open Access Button)
            </li>
            <li>
                <a href="https://nxref.com/">nXr reference manager</a>
            </li>
            <li>
                <a href="https://zenodo.org/record/1441072#.XTPinZNKjUI">OpenAIRE DOIBoost</a>
            </li>
            <li>
                <a href="https://cran.r-project.org/web/packages/roadoi/vignettes/intro.html">R client for our API</a>
            </li>
            <li>
                <a href="https://skepticalscience.com/">Skeptical Science</a>
            </li>
            <li>
                <a href="https://www.zotero.org/blog/improved-pdf-retrieval-with-unpaywall-integration/">Zotero</a>
            </li>
        </ul>

    </div>
</template>

<script>
    export default {
        name: "Integrations",
        metaInfo: {
            title: "Integrations"
        }
    }
</script>

<style scoped>
    h2 {
        margin-bottom: 0;
        padding-bottom: 0;
    }
    ul {
        margin-top: 0;
    }
    p {
        margin-top: 0;
    }

</style>
